import type { TImage } from "@input-output-hk/essential-cardano-models";
import { FastFieldProps, FormikHandlers } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ApiErrorResponse } from "../../api-utils/errors";
import { uploadImage } from "../../api-utils/upload-image";
import { MaybeApiError } from "../../hooks/useSWR";
import { getPrefferedImageUrl } from "../../utils/image";
import { Svg, SvgProps } from "../icons/Svg";
import TrashIcon from "../icons/Trash";
import Image from "../image/Image";
import Text from "../text/Text";
import { FormFieldElement } from "../contribute/FormFieldElement";
import { FormFieldLabel } from "../contribute/FormFieldLabel";
import LoadingSpinner from "../LoadingSpinner";
import { pxToRem } from "@input-output-hk/px-to-rem";

type Props = {
  name: string;
  label: string;
  id: string;
  disabled?: boolean;
  onUploadError?: (error: ApiErrorResponse) => void;
} & FastFieldProps &
  Pick<FormikHandlers, "getFieldHelpers">;

const ImageUploadField: React.FC<Props> = ({
  name,
  label,
  field,
  getFieldHelpers,
  disabled,
  onUploadError
}) => {
  const { setValue } = getFieldHelpers(name);
  const [image, setImage] = useState<TImage | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!image && field.value) {
      setLoading(true);
      fetch(`/api/image/${encodeURIComponent(field.value)}`)
        .then((res) => {
          if (res.ok) {
            return res.json() as Promise<MaybeApiError<{ data: TImage }>>;
          }
        })
        .then((json) => {
          if (json && "data" in json && !!json.data) {
            setImage(json.data);
          }
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const removeImage = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setValue("");
    setImage(null);
  }, [setValue]);

  const sendImage = useCallback(
    (file: File) => {
      setLoading(true);
      uploadImage(file)
        .then((resp) => {
          if ("image" in resp) {
            setValue(resp.image._canonicalUri);
            setImage(resp.image);
          } else if ("error" in resp) {
            removeImage();
            onUploadError?.(resp);
          }
        })
        .finally(() => setLoading(false));
    },
    [setValue, removeImage, onUploadError]
  );

  const handleChangeInput: React.ChangeEventHandler<HTMLInputElement> = (
    evt
  ) => {
    const file = evt.currentTarget?.files?.[0];
    if (file) {
      sendImage(file);
    } else {
      setImage(null);
    }
  };

  const handleDrop: React.DragEventHandler = (evt) => {
    evt.preventDefault();
    const file = evt.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      sendImage(file);
    }

    setIsHovering(false);
  };

  const handleDragOver: React.DragEventHandler = (evt) => evt.preventDefault();
  const handleDragEnterDropzone: React.DragEventHandler = () =>
    setIsHovering(true);
  const handleDragLeaveDropzone: React.DragEventHandler = () =>
    setIsHovering(false);

  return (
    <FormFieldElement data-upload-image>
      <FormFieldLabel>{label}</FormFieldLabel>
      <Element
        onClick={image ? removeImage : () => inputRef.current?.click?.()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnterDropzone}
        onDragLeave={handleDragLeaveDropzone}
        onDrop={handleDrop}
        data-ishovering={isHovering}
        data-disabled={disabled}
      >
        {loading && (
          <LoadingIndicator>
            <LoadingSpinner size="large" />
          </LoadingIndicator>
        )}
        <HiddenFileInput
          ref={inputRef}
          type="file"
          onChange={handleChangeInput}
        />
        <ImageWrapper>
          {image && (
            <Image
              data-image
              alt={image.name}
              height={image.height}
              width={image.width}
              blurhash={image._blurhash?.hash}
              src={getPrefferedImageUrl(image)}
            />
          )}
        </ImageWrapper>

        {!disabled && !image && !loading && (
          <MessageOuter>
            <AddIcon />
            <Message>Upload from computer</Message>
          </MessageOuter>
        )}
        {image && (
          <MessageOuter data-hover-only>
            <TrashIcon width={45} height={45} />
            <Message>Remove image</Message>
          </MessageOuter>
        )}
      </Element>
    </FormFieldElement>
  );
};

const Element = styled.div`
  position: relative;
  width: 100%;
  height: calc(100% - ${pxToRem(22)} - ${pxToRem(4)});
  border: ${pxToRem(2)} dashed var(--input-border-color);
  background-color: var(--input-background-color);
  border-radius: ${pxToRem(5)};
  cursor: pointer;
  &[data-disabled="true"] {
    pointer-events: none;
  }

  &[data-ishovering="true"] {
    border-color: hotpink;
  }
`;

const ImageWrapper = styled.div`
  object-fit: contain;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const MessageOuter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
  color: var(--text-color-muted);
  filter: drop-shadow(0 ${pxToRem(4)} ${pxToRem(6)} #efefef7f);

  *:not(:hover) > &[data-hover-only] {
    display: none;
  }
  &[data-hover-only] {
    mix-blend-mode: luminosity;
  }
`;
const Message = styled(Text)`
  display: block;
`;

function AddIcon(props: SvgProps) {
  return (
    <Svg
      width={45}
      height={45}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        stroke="currentColor"
        strokeWidth={4}
        d="M23.43.017v44M44.445 21.587h-44"
      />
    </Svg>
  );
}

const LoadingIndicator = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default ImageUploadField;
